import type { ReactNode } from 'react';
import { isValidElement, memo, useMemo } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyParams = Record<string, any>;

export type ParamsValidator<TParams extends AnyParams> = (params: TParams) => boolean;

interface RouteParamsValidatorCommonProps<TParams extends AnyParams> {
  /**
   * Callback that validates route parameters. If the result is `true` then navigation to route is allowed.
   */
  validate: ParamsValidator<TParams>;
}

interface PropsWithRedirect {
  /**
   * The path to which the user will be redirected if the param value is invalid.
   */
  redirectPath: string;
  invalidStateElement?: never;
}

interface PropsWithElement {
  /**
   * Element to be used when the param is invalid.
   */
  invalidStateElement: ReactNode;
  redirectPath?: never;
}

type RouteParamsValidatorProps<TParams extends AnyParams> = RouteParamsValidatorCommonProps<TParams> &
  (PropsWithElement | PropsWithRedirect);

const RouteParamsValidator = <TParams extends AnyParams>({ validate, ...restProps }: RouteParamsValidatorProps<TParams>) => {
  const params = useParams<TParams>();

  const valid = useMemo(() => validate(params), [params, validate]);

  const invalidStateElement = useMemo(() => {
    if (restProps.redirectPath) {
      return <Navigate to={restProps.redirectPath} replace />;
    }

    if (!isValidElement(restProps.invalidStateElement)) {
      return null;
    }

    return restProps.invalidStateElement;
  }, [restProps.invalidStateElement, restProps.redirectPath]);

  if (!valid) {
    return invalidStateElement;
  }

  return <Outlet />;
};

export default memo(RouteParamsValidator) as typeof RouteParamsValidator;
